import React, {FC, useEffect, useState} from 'react';
import Box from '@material-ui/core/Box';
import {withLocalization} from 'epic-localization/src/context/LocalizationContext';
import {Filter} from '@epic-ui/icons';
import {useTheme} from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import LineChart from '../components/chart/LineChart';
import {Widget} from '../../../../common/widget';
import WidgetTooltip from '../components/WidgetTooltip';
import WidgetDownloadButton from '../components/WidgetDownloadButton';
import HorizontalLabel from '../components/HorizontalLabel';
import WidgetGameSelect from '../../../../game-select/WidgetGameSelect';
import WidgetDatePicker from '../../../../widget-date-picker/WidgetDatePicker';
import IconButton from '../../../../common/IconButton';
import IntervalRadioGroupPlaceholder from '../components/interval-radio-group/IntervalRadioGroupPlaceholder';
import IntervalSelectPlaceholder from '../components/interval-radio-group/IntervalSelectPlaceholder';

import useChartTrends from '../hooks/useChartTrends';
import useReturnPlayersData from './useReturnPlayersData';
import {prepareLineChartExportData} from '../exportUtils';
import {useRangePickerData} from '../../../../../components/common/date-range-picker';
import {
    dateFormatter,
    getWidgetTimeRangeDates,
    axisPercentFormatter,
    percentFormatter
} from '../utils';
import {TIME_SCALES} from '../../../../constants';

import {WidgetProps} from '../types';
import {DateRange, Status, TimeScale} from '../../../../../types';

import {ChartWrapper, OptionWrapper} from '../styles';
import {LEGENDS_LIMIT, xAxisFormatters} from '../constants';
import {PDFPictureTrap} from '../export/PDFExportProvider';
import useWidgetSelectedGames from '../hooks/useWidgetSelectedGames';

const TIME_SCALE = TimeScale.DAILY;

const CODES = {
    title: 'epic.affiliate.fortnite_games.return_players',
    widgetTooltip: 'epic.affiliate.fortnite_games.return_players.tooltip',
    timeScale: 'epic.affiliate.fortnite_games.time_scale.',
    timeScaleTooltip: 'epic.affiliate.fortnite_games.time_scale.tooltip',
    showMe: 'epic.affiliate.fortnite_games.show_me'
};

const ReturnPlayersWidget: FC<WidgetProps> = ({
    selectedDate: selected,
    startDate: start,
    endDate: end,
    selectedGames: selectedGamesProps,
    gamesList,
    getMessage,
    keysMap,
    gamesLoading
}: WidgetProps) => {
    const theme = useTheme();
    const isFmdBreakpoint = useMediaQuery(theme.breakpoints.up('fmd'));
    const isXlBreakpoint = useMediaQuery(theme.breakpoints.up('xl'));

    const [isPreselectedRange, setIsPreselectedRange] = useState(true);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [selectedGames, handleSelectGames] = useWidgetSelectedGames(selectedGamesProps);

    const {selectedDate, startDate, endDate, setCalendar} = useRangePickerData(
        getWidgetTimeRangeDates(TimeScale.DAILY)
    );

    const {data, loading, error, reload} = useReturnPlayersData({
        startDate,
        endDate,
        timeScale: TIME_SCALE,
        selectedGames
    });

    const chartData = data[TIME_SCALE].list || [];
    const emptyDataKeys = data[TIME_SCALE].empty;

    const trends = useChartTrends({data: chartData, xAxis: 'time'});

    const timeScaleOptions = TIME_SCALES.slice(1).map(value => ({
        label: getMessage(`${CODES.timeScale}${value}`),
        value
    }));

    // Handlers
    const handleRangeChange = (dateRange: DateRange) => {
        setCalendar(dateRange);
        setIsPreselectedRange(false);
    };

    const handleMobileMenuClick = () => {
        setIsMobileMenuOpen(!isMobileMenuOpen);
    };

    // Effects
    useEffect(() => {
        if (!isPreselectedRange || (!isMobileMenuOpen && !isFmdBreakpoint)) {
            setCalendar({startDate: start, endDate: end, selectedDate: selected});
        }
    }, [start, end, selected]);

    useEffect(() => {
        setIsMobileMenuOpen(false);
    }, [isFmdBreakpoint]);

    const GameSelect = (
        <WidgetGameSelect options={gamesList} value={selectedGames} onChange={handleSelectGames} />
    );

    const TimeSelect = (
        <IntervalSelectPlaceholder
            tooltip={getMessage(CODES.timeScaleTooltip)}
            value={timeScaleOptions[0]}
        />
    );

    const DatePicker = (
        <WidgetDatePicker
            timeScale={TimeScale.DAILY}
            selectedDate={selectedDate}
            startDate={startDate}
            endDate={endDate}
            onRangeChange={handleRangeChange}
        />
    );

    return (
        <PDFPictureTrap id={CODES.title} index={3}>
            <div>
                <Widget
                    height={530}
                    header={getMessage(CODES.title)}
                    padding="15px"
                    tooltip={<WidgetTooltip>{getMessage(CODES.widgetTooltip)}</WidgetTooltip>}
                    status={error ? Status.ERROR : undefined}
                    message={getMessage(error) || error}
                    overflow="visible"
                    retry={reload}
                    actions={
                        <>
                            {isFmdBreakpoint ? (
                                <>
                                    {isXlBreakpoint ? (
                                        <HorizontalLabel>
                                            {getMessage(CODES.showMe)}
                                            <IntervalRadioGroupPlaceholder
                                                tooltip={getMessage(CODES.timeScaleTooltip)}
                                                options={timeScaleOptions}
                                                value={timeScaleOptions[0]}
                                            />
                                        </HorizontalLabel>
                                    ) : (
                                        <Box mx={1.5}>{TimeSelect}</Box>
                                    )}

                                    <Box mx={1.5} width={233}>
                                        {DatePicker}
                                    </Box>
                                    <Box mx={1.5} width={178}>
                                        {GameSelect}
                                    </Box>
                                </>
                            ) : (
                                <IconButton
                                    onClick={handleMobileMenuClick}
                                    isOpen={isMobileMenuOpen}>
                                    <Filter />
                                </IconButton>
                            )}
                            <WidgetDownloadButton
                                areaId={CODES.title}
                                fileName={getMessage(CODES.title)}
                                data={chartData}
                                prepareCSVData={prepareLineChartExportData(keysMap)}
                                disabled={loading || gamesLoading || !!error}
                            />
                        </>
                    }
                    options={
                        isMobileMenuOpen &&
                        !isFmdBreakpoint && (
                            <OptionWrapper>
                                {TimeSelect}
                                {DatePicker}
                                {GameSelect}
                            </OptionWrapper>
                        )
                    }>
                    <ChartWrapper padding="0">
                        <LineChart
                            selectedGames={selectedGames}
                            height={470}
                            data={chartData}
                            trends={trends}
                            limit={LEGENDS_LIMIT}
                            loading={loading || gamesLoading}
                            keysMap={keysMap}
                            tooltipFormatter={percentFormatter}
                            yTickFormatter={axisPercentFormatter}
                            xTickFormatter={xAxisFormatters[TimeScale.DAILY]}
                            yAxisProps={{
                                domain: [0, 100],
                                tickCount: 11
                            }}
                            emptyDataKeys={emptyDataKeys}
                            tooltipLabelFormatter={dateFormatter()}
                        />
                    </ChartWrapper>
                </Widget>
            </div>
        </PDFPictureTrap>
    );
};

export default withLocalization(ReturnPlayersWidget);
